import {
  Component,
  ChangeDetectionStrategy,
  DestroyRef,
  ElementRef,
  HostListener,
  ViewChild,
  inject,
  signal,
  computed,
  effect,
} from '@angular/core';
import { FormBuilder, ReactiveFormsModule, Validators } from '@angular/forms';
import { HttpErrorResponse } from '@angular/common/http';
import { Router } from '@angular/router';
import {
  EMPTY,
  Observable,
  Subscription,
  catchError,
  merge,
  map as rxMap,
  of,
  retry,
  switchMap,
  take,
  tap,
} from 'rxjs';

import {
  MatDialog,
  MatDialogModule,
  MatDialogRef,
} from '@angular/material/dialog';
import { NoopScrollStrategy } from '@angular/cdk/overlay';
import { MatIconModule } from '@angular/material/icon';
import { MatButtonModule } from '@angular/material/button';
import { MatToolbarModule } from '@angular/material/toolbar';
import { MatExpansionModule } from '@angular/material/expansion';
import { MatSelectModule } from '@angular/material/select';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatRadioModule } from '@angular/material/radio';
import {
  MatCheckboxChange,
  MatCheckboxModule,
} from '@angular/material/checkbox';
import { TranslateModule, TranslateService } from '@ngx-translate/core';

import { LanguageService } from '@services/language.service';
import { MemberService } from '@services/member.service';
import {
  ConfirmDialogComponent,
  ConfirmDialogData,
} from '@shared/confirm-dialog/confirm-dialog.component';
import { GuidedCourseCreationService } from '@services/guided-course-creation.service';
import { GtmWrapperService } from '@services/gtm-wrapper.service';
import { Member } from '@models/member.model';
import { ElearnioLanguageSpanModule } from '@shared/elearnio-language-icon/elearnio-language-span.module';
import { GraphicOptionCardComponent } from '@shared/graphic-option-card/graphic-option-card.component';
import { TooltipButtonComponent } from '@app/core/components/tooltip-button/tooltip-button.component';
import { type2img } from '@helper/h5p-icons-helper';

import {
  COURSE_INTENT_OPTIONS,
  TARGET_AUDIENCE_OPTIONS,
  COURSE_LENGTH_TYPE_OPTIONS,
  COURSE_LENGTH_TYPES,
  getModuleTypeConfig,
  formatLearningObjectives,
  mapOutlineApiResponse,
  CourseIntent,
  CourseLengthType,
  TargetAudience,
  OptionConfig,
  CourseOutline,
  CourseSectionOutline,
  OutlineApiResponse,
  SkeletonRequestPayload,
  SkeletonResponse,
  SectionContentModule,
  getAiErrorTranslationKey,
  AI_ERROR_CODES,
  AiErrorCode,
  isAiContentSupported,
  isAiImageSupported,
  DOCUMENT_MODES,
  DOCUMENT_UPLOAD_STATES,
  DocumentMode,
  DocumentUploadStatus,
  IDLE_DOCUMENT_STATUS,
  toAnalyticsContentMode,
  toAnalyticsStructureMode,
} from '@core/constants/guided-course-creation.constants';
import { CourseCreationErrorDialogComponent } from '../course-creation-error-dialog/course-creation-error-dialog.component';
import { LoadingSpinnerComponent } from '@shared/loading-spinner/loading-spinner.component';
import { OutlineChatComponent } from './outline-chat/outline-chat.component';
import { DocumentUploadComponent } from './document-upload/document-upload.component';
import { AiSettingsPanelComponent } from './ai-settings-panel/ai-settings-panel.component';
import { CreationProgressComponent } from './creation-progress/creation-progress.component';
import { TagComponent } from '@shared/tag/tag.component';

type DialogPage = 'form' | 'outline';

@Component({
  selector: 'app-guided-course-creation-dialog',
  templateUrl: './guided-course-creation-dialog.component.html',
  styleUrls: ['./guided-course-creation-dialog.component.scss'],
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    ReactiveFormsModule,
    MatDialogModule,
    MatIconModule,
    MatButtonModule,
    MatToolbarModule,
    MatExpansionModule,
    MatSelectModule,
    MatFormFieldModule,
    MatRadioModule,
    MatCheckboxModule,
    TranslateModule,
    TooltipButtonComponent,
    ElearnioLanguageSpanModule,
    GraphicOptionCardComponent,
    LoadingSpinnerComponent,
    OutlineChatComponent,
    DocumentUploadComponent,
    AiSettingsPanelComponent,
    CreationProgressComponent,
    TagComponent,
  ],
})
export class GuidedCourseCreationDialogComponent {
  private readonly dialogRef = inject(
    MatDialogRef<GuidedCourseCreationDialogComponent>,
  );
  private readonly fb = inject(FormBuilder);
  private readonly languageService = inject(LanguageService);
  private readonly memberService = inject(MemberService);
  private readonly translateService = inject(TranslateService);
  private readonly matDialog = inject(MatDialog);
  private readonly guidedCourseCreationService = inject(
    GuidedCourseCreationService,
  );
  private readonly gtmService = inject(GtmWrapperService);
  private readonly router = inject(Router);
  private readonly destroyRef = inject(DestroyRef);

  readonly intentOptions: readonly OptionConfig<CourseIntent>[] =
    COURSE_INTENT_OPTIONS;
  readonly audienceOptions: readonly OptionConfig<TargetAudience>[] =
    TARGET_AUDIENCE_OPTIONS;
  readonly lengthTypeOptions: readonly OptionConfig<CourseLengthType>[] =
    COURSE_LENGTH_TYPE_OPTIONS;
  readonly availableLanguages = this.languageService.activeLanguages();
  @ViewChild('scrollContainer')
  private scrollContainer!: ElementRef<HTMLElement>;

  readonly sessionId = signal<string | null>(null);
  readonly isSessionLoading = signal(true);
  readonly currentPage = signal<DialogPage>('form');
  readonly outline = signal<CourseOutline | null>(null);
  readonly skeletonResponse = signal<SkeletonResponse | null>(null);
  readonly isLoading = signal(false);
  readonly isCreatingCourse = signal(false);
  readonly creationProgress = this.guidedCourseCreationService.creationProgress;
  readonly isSectionsLoading = signal(false);
  readonly sectionsError = signal(false);
  private readonly failedSectionIndices = signal<number[]>([]);
  readonly contentGenerationEnabled = signal(true);
  readonly imageGenerationEnabled = signal(false);
  readonly isAiContentSupported = isAiContentSupported;
  readonly isAiImageSupported = isAiImageSupported;
  readonly learningObjectives = computed(() =>
    formatLearningObjectives(this.outline()?.learningObjectives ?? []),
  );
  readonly documentStatus = signal<DocumentUploadStatus>(IDLE_DOCUMENT_STATUS);
  readonly isDocumentUploading = computed(
    () => this.documentStatus().state === DOCUMENT_UPLOAD_STATES.UPLOADING,
  );
  readonly hasDocument = computed(
    () => this.documentStatus().state === DOCUMENT_UPLOAD_STATES.DONE,
  );
  readonly activeDocumentMode = computed<DocumentMode>(() =>
    this.hasDocument()
      ? this.documentStatus().documentMode
      : DOCUMENT_MODES.IGNORED,
  );
  readonly contentModeOverride = signal<DocumentMode | null>(null);
  readonly effectiveContentMode = computed<DocumentMode>(
    () => this.contentModeOverride() ?? this.activeDocumentMode(),
  );
  private readonly initialDisableClose = this.dialogRef.disableClose;
  private sessionSubscription: Subscription | null = null;
  private generateSubscription: Subscription | null = null;
  private createCourseSubscription: Subscription | null = null;

  readonly member = signal<Member | undefined>(undefined);
  readonly termsAlreadyAccepted = signal(false);

  readonly form = this.fb.nonNullable.group({
    topic: ['', Validators.required],
    language: [this.languageService.currentLanguage(), Validators.required],
    intent: ['' as CourseIntent | '', Validators.required],
    audience: [
      [] as TargetAudience[],
      [Validators.required, Validators.minLength(1)],
    ],
    lengthType: this.fb.nonNullable.control<CourseLengthType>(
      COURSE_LENGTH_TYPES.STANDARD,
      Validators.required,
    ),
  });

  readonly formSummary = computed(() => {
    const raw = this.form.getRawValue();
    const intentOption = this.intentOptions.find(o => o.id === raw.intent);
    const audienceLabels = raw.audience
      .map(a => {
        const option = this.audienceOptions.find(o => o.id === a);
        return option ? this.translateService.instant(option.titleKey) : a;
      })
      .join(', ');

    return {
      intentLabel: intentOption
        ? this.translateService.instant(intentOption.titleKey)
        : '',
      audienceLabel: audienceLabels,
    };
  });

  constructor() {
    this.destroyRef.onDestroy(() => {
      this.sessionSubscription?.unsubscribe();
      this.generateSubscription?.unsubscribe();
      this.createCourseSubscription?.unsubscribe();
    });

    this.memberService
      .current()
      .pipe(take(1))
      .subscribe(member => {
        if (!member) return;
        this.member.set(member);
        this.termsAlreadyAccepted.set(member.aiTermsAgreedTo);
      });

    this.createSession();

    effect(() => {
      if (this.currentPage() !== 'outline') return;
      const wsc = window.WEBSPELLCHECKER;
      if (!wsc) return;
      wsc.destroyAll();
      wsc.stopAutoSearch();
    });
  }

  @HostListener('window:beforeunload', ['$event'])
  onBeforeUnload(event: BeforeUnloadEvent): void {
    if (
      this.currentPage() === 'outline' ||
      this.isLoading() ||
      this.isSectionsLoading() ||
      this.isDocumentUploading()
    ) {
      event.preventDefault();
    }
  }

  get isFormValid(): boolean {
    return this.form.valid;
  }

  isAudienceSelected(id: TargetAudience): boolean {
    return this.form.controls.audience.value.includes(id);
  }

  toggleAudience(id: TargetAudience): void {
    const current = this.form.controls.audience.value;
    const next = current.includes(id)
      ? current.filter(a => a !== id)
      : [...current, id];
    this.form.controls.audience.setValue(next);
  }

  onDocumentStatusChange(status: DocumentUploadStatus): void {
    this.documentStatus.set(status);
  }

  onTopicSuggestionSelected(topic: string): void {
    const current = this.form.controls.topic.value.trim();
    this.form.controls.topic.setValue(current ? `${current}, ${topic}` : topic);
  }

  onGenerate(): void {
    const sessionId = this.sessionId();
    if (
      !this.form.valid ||
      !this.member()?.aiTermsAgreedTo ||
      !sessionId ||
      this.isDocumentUploading()
    )
      return;

    const raw = this.form.getRawValue();
    if (!raw.intent) return;

    const documentUploaded = this.hasDocument();
    this.gtmService.pushTag({
      event: 'guided_course_outline_generated',
      document_uploaded: documentUploaded,
      ...(documentUploaded
        ? {
            structure_mode: toAnalyticsStructureMode(
              this.documentStatus().documentMode,
            ),
          }
        : {}),
    });

    this.isLoading.set(true);

    const payload: SkeletonRequestPayload = {
      topic: raw.topic,
      language: raw.language,
      intent: raw.intent,
      audience: raw.audience,
      length_type: raw.lengthType,
      document_mode: this.activeDocumentMode(),
    };

    this.generateSubscription = this.guidedCourseCreationService
      .generateSkeleton(sessionId, payload)
      .pipe(
        take(1),
        tap(response => {
          this.skeletonResponse.set(response);
          this.setSummary(response);
          this.isLoading.set(false);
        }),
        catchError((err: HttpErrorResponse) => {
          this.isLoading.set(false);
          this.showSkeletonErrorDialog(this.getAiErrorCode(err));
          return EMPTY;
        }),
        switchMap(skeleton => {
          this.sectionsError.set(false);
          this.failedSectionIndices.set([]);
          this.isSectionsLoading.set(true);
          const sections = skeleton.sections.map((s, index) => ({
            sectionId: s.section_id,
            index,
          }));

          return this.fetchSectionContents(sessionId, sections);
        }),
      )
      .subscribe(this.sectionContentObserver());
  }

  private retrySections(): void {
    this.generateSubscription?.unsubscribe();

    const sessionId = this.sessionId();
    const skeleton = this.skeletonResponse();
    if (!skeleton || !sessionId) return;

    const failedIndices = this.failedSectionIndices();
    if (!failedIndices.length) return;

    this.sectionsError.set(false);
    this.failedSectionIndices.set([]);
    this.isSectionsLoading.set(true);
    const sections = failedIndices.map(index => ({
      sectionId: skeleton.sections[index].section_id,
      index,
    }));

    this.generateSubscription = this.fetchSectionContents(
      sessionId,
      sections,
      0,
    ).subscribe(this.sectionContentObserver());
  }

  private sectionContentObserver() {
    return {
      next: (result: {
        index: number;
        sectionModules: SectionContentModule[];
        error: boolean;
      }) => {
        this.handleSectionResult(
          result.index,
          result.sectionModules,
          result.error,
        );
      },
      complete: () => {
        this.onSectionFetchComplete();
      },
    };
  }

  private handleSectionResult(
    index: number,
    sectionModules: SectionContentModule[],
    error: boolean,
  ): void {
    if (error) {
      this.failedSectionIndices.update(ids => [...ids, index]);
    } else {
      this.setSectionAt(index, sectionModules);
    }
  }

  private onSectionFetchComplete(): void {
    if (this.failedSectionIndices().length > 0) {
      this.isSectionsLoading.set(false);
      this.sectionsError.set(true);
      this.showSectionsErrorDialog(AI_ERROR_CODES.GENERIC);
    } else {
      this.isSectionsLoading.set(false);
    }
  }

  private fetchSectionContents(
    sessionId: string,
    sections: { sectionId: number; index: number }[],
    retryCount = 3,
  ) {
    const documentMode = this.activeDocumentMode();

    return merge(
      ...sections.map(({ sectionId, index }) =>
        this.guidedCourseCreationService
          .generateSectionContent(sessionId, sectionId, documentMode)
          .pipe(
            take(1),
            retry(retryCount),
            rxMap(sectionModules => ({ index, sectionModules, error: false })),
            catchError(() =>
              of({
                index,
                sectionModules: [] as SectionContentModule[],
                error: true,
              }),
            ),
          ),
      ),
    );
  }

  private setSummary(response: SkeletonResponse): void {
    this.outline.set({
      title: response.course_title,
      totalTimeMinutes: response.estimated_duration_minutes,
      learningObjectives: response.learning_objectives ?? [],
      sections: response.sections.map(s => ({
        headline: s.section_title,
        modules: [],
      })),
    });
    this.currentPage.set('outline');
    this.scrollContainer?.nativeElement?.scrollTo(0, 0);
  }

  private setSectionAt(
    index: number,
    sectionModules: SectionContentModule[],
  ): void {
    const current = this.outline();
    if (!current) return;

    const section: CourseSectionOutline = {
      headline: current.sections[index]?.headline ?? '',
      modules: sectionModules.map(p => {
        const config = getModuleTypeConfig(p.type);
        return {
          name: p.title,
          moduleType: config?.displayKey ?? p.type,
          rawModuleType: p.type,
          estimatedTimeMinutes: p.estimated_duration_minutes,
          whatToCover: p.what_to_cover,
          moduleId: p.module_id,
        };
      }),
    };

    const sections = [...current.sections];
    sections[index] = section;
    this.outline.set(this.enrichOutlineIcons({ ...current, sections }));
  }

  onBack(): void {
    this.showUnsavedChangesConfirm()
      .pipe(take(1))
      .subscribe(confirmed => {
        if (confirmed) {
          this.generateSubscription?.unsubscribe();
          this.currentPage.set('form');
          this.outline.set(null);
          this.skeletonResponse.set(null);
          this.isLoading.set(false);
          this.isSectionsLoading.set(false);
          this.sectionsError.set(false);
          this.failedSectionIndices.set([]);
          this.contentGenerationEnabled.set(true);
          this.imageGenerationEnabled.set(false);
          this.contentModeOverride.set(null);
          this.documentStatus.set(IDLE_DOCUMENT_STATUS);
          this.createSession();
        }
      });
  }

  onOutlineUpdated(outline: OutlineApiResponse): void {
    this.outline.set(this.enrichOutlineIcons(mapOutlineApiResponse(outline)));
  }

  onWhatToCoverChange(
    sectionIndex: number,
    moduleIndex: number,
    event: Event,
  ): void {
    const newValue = (event.target as HTMLTextAreaElement).value;
    const current = this.outline();
    if (!current) return;

    const mod = current.sections[sectionIndex]?.modules[moduleIndex];
    if (!mod || mod.whatToCover === newValue) return;

    const sections = [...current.sections];
    const modules = [...sections[sectionIndex].modules];
    modules[moduleIndex] = { ...mod, whatToCover: newValue };
    sections[sectionIndex] = { ...sections[sectionIndex], modules };
    this.outline.set({ ...current, sections });

    const sid = this.sessionId();
    if (sid && mod.moduleId) {
      this.guidedCourseCreationService
        .updateWhatToCover(sid, mod.moduleId, newValue)
        .pipe(take(1))
        .subscribe();
    }
  }

  onCreateCourse(): void {
    if (this.isLoading()) return;
    if (!this.form.valid) return;
    const raw = this.form.getRawValue();
    const currentOutline = this.outline();
    const member = this.member();
    if (!currentOutline || !member) return;

    this.gtmService.pushTag({
      event: 'guided_course_created',
      content_mode: toAnalyticsContentMode(
        this.contentGenerationEnabled(),
        this.effectiveContentMode(),
      ),
      images_generated: this.imageGenerationEnabled(),
    });

    this.isLoading.set(true);
    this.setCreationBlocking(true);
    this.createCourseSubscription = this.guidedCourseCreationService
      .create({
        title: currentOutline.title,
        language: raw.language,
        estimatedTimeInMinutes: currentOutline.totalTimeMinutes,
        outline: currentOutline,
        author: member,
        contentGenerationEnabled: this.contentGenerationEnabled(),
        imageGenerationEnabled: this.imageGenerationEnabled(),
        contentMode: this.effectiveContentMode(),
        sessionId: this.sessionId()!,
      })
      .subscribe({
        next: course => {
          this.dialogRef.close();
          this.router.navigate(['/courses', course.id, 'content'], {
            queryParams: { fromGcc: true },
          });
        },
        error: (err: unknown) => {
          this.isLoading.set(false);
          this.setCreationBlocking(false);
          this.showCreationErrorDialog(err);
        },
      });
  }

  // Without a cancel button the overlay is the only state, so Esc must not tear
  // the dialog down while the AI is still working.
  private setCreationBlocking(blocking: boolean): void {
    this.isCreatingCourse.set(blocking);
    this.dialogRef.disableClose = blocking || this.initialDisableClose;
  }

  onClose(): void {
    if (this.currentPage() === 'outline') {
      this.showUnsavedChangesConfirm()
        .pipe(take(1))
        .subscribe(confirmed => {
          if (confirmed) {
            this.dialogRef.close();
          }
        });
    } else {
      this.dialogRef.close();
    }
  }

  private showUnsavedChangesConfirm(): Observable<boolean> {
    const data: ConfirmDialogData = {
      message: this.translateService.instant(
        'common.confirmation.unsaved_changes',
      ),
      title: this.translateService.instant('common.confirmation.cancel_title'),
    };
    return this.matDialog
      .open(ConfirmDialogComponent, {
        data,
        panelClass: 'elearnio-mat-dialog-panel-class',
        scrollStrategy: new NoopScrollStrategy(),
      })
      .afterClosed();
  }

  onTermsAndConditionsChange(event: MatCheckboxChange): void {
    const member = this.member();
    if (member) {
      member.aiTermsAgreedTo = event.checked;
      if (member.hasDirtyAttributes) {
        member.save().pipe(take(1)).subscribe();
      }
    }
  }

  private showCreationErrorDialog(err: unknown): void {
    this.showErrorDialogWithRetry(this.extractErrorMessage(err), () =>
      this.onCreateCourse(),
    );
  }

  private extractErrorMessage(err: unknown): string {
    if (err instanceof Error) return err.message;
    if (
      typeof err === 'object' &&
      err !== null &&
      'message' in err &&
      typeof (err as Record<string, unknown>)['message'] === 'string'
    ) {
      return (err as Record<string, unknown>)['message'] as string;
    }
    return this.translateService.instant(
      'guidedCourseCreation.errorDialog.unknownError',
    );
  }

  private showSkeletonErrorDialog(errorCode: AiErrorCode): void {
    this.showErrorDialogWithRetry(
      this.translateService.instant(getAiErrorTranslationKey(errorCode)),
      () => this.onGenerate(),
    );
  }

  private showSectionsErrorDialog(errorCode: AiErrorCode): void {
    this.showErrorDialogWithRetry(
      this.translateService.instant(getAiErrorTranslationKey(errorCode)),
      () => this.retrySections(),
    );
  }

  private getAiErrorCode(err: HttpErrorResponse): AiErrorCode {
    return err.status === 408 || err.status === 504
      ? AI_ERROR_CODES.TIMEOUT
      : AI_ERROR_CODES.GENERIC;
  }

  private showErrorDialogWithRetry(
    errorMessage: string,
    retryFn: () => void,
    dismissFn?: () => void,
  ): void {
    const ref = this.matDialog.open(CourseCreationErrorDialogComponent, {
      data: { errorMessage },
      panelClass: 'elearnio-mat-dialog-panel-class',
      autoFocus: false,
    });

    ref
      .afterClosed()
      .pipe(take(1))
      .subscribe((retry: boolean) => {
        if (retry) {
          retryFn();
        } else {
          dismissFn?.();
        }
      });
  }

  private createSession(): void {
    this.isSessionLoading.set(true);
    this.sessionSubscription = this.guidedCourseCreationService
      .createSession()
      .pipe(take(1))
      .subscribe({
        next: id => {
          this.sessionId.set(id);
          this.isSessionLoading.set(false);
        },
        error: () => {
          this.isSessionLoading.set(false);
          this.showErrorDialogWithRetry(
            this.translateService.instant(
              getAiErrorTranslationKey(AI_ERROR_CODES.GENERIC),
            ),
            () => this.createSession(),
            () => this.dialogRef.close(),
          );
        },
      });
  }

  private enrichOutlineIcons(outline: CourseOutline): CourseOutline {
    return {
      ...outline,
      sections: outline.sections.map(section => ({
        ...section,
        modules: section.modules.map(mod => {
          const config = getModuleTypeConfig(mod.rawModuleType);
          return {
            ...mod,
            iconPath: type2img(config?.iconType ?? mod.moduleType),
          };
        }),
      })),
    };
  }
}
